import React from 'react';
import { CheckCircle2, Users, UserPlus, Clock, Search } from 'lucide-react';
import { useAdvisoryTeamMember } from './useAdvisoryTeamMember';

const CATEGORY_CONFIG = {
  cbv: {
    label: 'CBV',
    fullLabel: 'Chartered Business Valuator (CBV)',
    description: 'A CBV can help you recast your EBITDA and formalize your business valuation.',
    genericDescription: 'A Chartered Business Valuator can help you formalize your business valuation and prepare for succession.',
    learnMore: 'Learn what a CBV does',
  },
  business_broker: {
    label: 'Business Broker',
    fullLabel: 'Business Broker',
    description: 'A broker can help you find qualified buyers and structure the deal.',
    genericDescription: 'A Business Broker can help you find qualified buyers, market your business, and negotiate the best deal.',
    learnMore: 'Learn what a Broker does',
  },
  lender: {
    label: 'Lender',
    fullLabel: 'Lender',
    description: 'A lender can help structure acquisition financing for your transition.',
    genericDescription: 'A Lender can help structure acquisition financing, vendor-takeback arrangements, or EOT funding.',
    learnMore: 'Learn about financing options',
  },
  cepa_coach: {
    label: 'CEPA Coach',
    fullLabel: 'CEPA Coach / Advisor',
    description: 'A CEPA coach can guide you through the full exit planning process.',
    genericDescription: 'A Certified Exit Planning Advisor can guide you through succession planning and value acceleration.',
    learnMore: 'Learn what a CEPA does',
  },
  accountant: {
    label: 'Accountant',
    fullLabel: 'Accountant',
    description: 'An accountant can help with tax-efficient exit structures and wealth gap analysis.',
    genericDescription: 'An Accountant can help you optimize your exit tax strategy, estate freeze, and capital gains exemptions.',
    learnMore: 'Learn about tax planning',
  },
  lawyer: {
    label: 'Lawyer',
    fullLabel: 'Lawyer',
    description: 'A lawyer can help with buy-sell agreements, IP protection, and deal documentation.',
    genericDescription: 'A Lawyer can help with share purchase agreements, non-competes, key employee retention, and deal documentation.',
    learnMore: 'Learn about legal support',
  },
};

export default function ReadyToConnectWidget({
  partnerCategory,
  state = 'generic', // 'assessment-context' | 'generic' | 'pending' | 'connected'
  assessmentScore,
  requestedTime,
  partnerName,
  partnerFirm,
  onRequestIntroduction,
  loading = false,
  className = '',
  // DEPRECATED: kept for backwards compatibility during migration. The widget
  // now reads team member data internally via useAdvisoryTeamMember(partnerCategory).
  // These props are ignored — consumers should drop them.
  // eslint-disable-next-line no-unused-vars
  teamMemberName: _deprecatedTeamMemberName,
  // eslint-disable-next-line no-unused-vars
  teamMemberStatus: _deprecatedTeamMemberStatus,
  // eslint-disable-next-line no-unused-vars
  teamMemberFirm: _deprecatedTeamMemberFirm,
}) {
  const config = CATEGORY_CONFIG[partnerCategory] || CATEGORY_CONFIG.cbv;

  // Apollo cache-first read of the matching advisory team member; null if role unfilled.
  const teamMember = useAdvisoryTeamMember(partnerCategory);
  const teamMemberName = teamMember?.name;
  const teamMemberStatus = teamMember?.status;
  const teamMemberFirm = teamMember?.firm;

  // Priority 0: Team member exists — check before marketplace states
  if (teamMemberName && teamMemberStatus) {
    // Off-platform: suppress marketplace entirely
    if (teamMemberStatus === 'off_platform') {
      return (
        <div className={`bg-card border border-border rounded-[16px] p-6 w-full ${className}`}>
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 bg-muted border border-border text-muted-foreground rounded-full px-3 py-1">
              <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40" />
              <span className="font-mono text-[11px] uppercase tracking-wide">
                Role covered
              </span>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground font-semibold text-sm">
                {teamMemberName.split(' ').map((n) => n[0]).join('')}
              </div>
              <div className="flex-1">
                <div className="text-[14px] text-foreground font-medium leading-tight">
                  {teamMemberName}
                </div>
                {teamMemberFirm && (
                  <div className="text-[13px] text-muted-foreground">
                    {teamMemberFirm}
                  </div>
                )}
              </div>
            </div>

            <p className="text-[14px] text-muted-foreground leading-relaxed">
              Your {config.label.toLowerCase()} is handled off-platform. No further action needed here.
            </p>
          </div>
        </div>
      );
    }

    // Active or Added: "You work with [Name]. Involve them?"
    if (teamMemberStatus === 'active' || teamMemberStatus === 'added') {
      const isActive = teamMemberStatus === 'active';
      return (
        <div className={`bg-card border border-border rounded-[16px] p-6 w-full ${className}`}>
          <div className="space-y-4">
            <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 ${
              isActive
                ? 'bg-[hsl(var(--chart-2))]/10 border border-[hsl(var(--chart-2))]/30 text-[hsl(var(--chart-2))]'
                : 'bg-blue-500/10 border border-blue-500/30 text-blue-500'
            }`}>
              {isActive ? <Users className="w-3 h-3" /> : <UserPlus className="w-3 h-3" />}
              <span className="font-mono text-[11px] uppercase tracking-wide">
                {isActive ? 'On your team' : 'Added'}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${
                isActive
                  ? 'bg-[hsl(var(--chart-2))]/10 text-[hsl(var(--chart-2))]'
                  : 'bg-blue-500/10 text-blue-500'
              }`}>
                {teamMemberName.split(' ').map((n) => n[0]).join('')}
              </div>
              <div className="flex-1">
                <div className="text-[14px] text-foreground font-medium leading-tight">
                  {teamMemberName}
                </div>
                {teamMemberFirm && (
                  <div className="text-[13px] text-muted-foreground">
                    {teamMemberFirm}
                  </div>
                )}
              </div>
            </div>

            <p className="text-[14px] text-muted-foreground leading-relaxed">
              {isActive
                ? `You work with ${teamMemberName.split(' ')[0]}. Want to involve them in this?`
                : `${teamMemberName.split(' ')[0]} is on your team. Invite them to collaborate on Succeedo.`}
            </p>

            <button
              type="button"
              onClick={() => { window.location.href = '/succeedo/teams'; }}
              className={`w-full rounded-lg py-3 font-medium text-[14px] transition-colors ${
                isActive
                  ? 'bg-[hsl(var(--chart-2))] text-white hover:bg-[hsl(var(--chart-2))]/90'
                  : 'bg-blue-500 text-white hover:bg-blue-500/90'
              }`}
            >
              {isActive ? 'View Your Team' : 'Invite to Collaborate'}
            </button>
          </div>
        </div>
      );
    }

    // Invited: pending acceptance
    if (teamMemberStatus === 'invited') {
      return (
        <div className={`bg-card border border-border rounded-[16px] p-6 w-full ${className}`}>
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 bg-[hsl(var(--succeedo-orange))]/10 border border-[hsl(var(--succeedo-orange))]/20 text-[hsl(var(--succeedo-orange))] rounded-full px-3 py-1">
              <Clock className="w-3 h-3" />
              <span className="font-mono text-[11px] uppercase tracking-wide">
                Invited
              </span>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[hsl(var(--succeedo-orange))]/10 flex items-center justify-center text-[hsl(var(--succeedo-orange))] font-semibold text-sm">
                {teamMemberName.split(' ').map((n) => n[0]).join('')}
              </div>
              <div className="flex-1">
                <div className="text-[14px] text-foreground font-medium leading-tight">
                  {teamMemberName}
                </div>
                {teamMemberFirm && (
                  <div className="text-[13px] text-muted-foreground">
                    {teamMemberFirm}
                  </div>
                )}
              </div>
            </div>

            <p className="text-[14px] text-muted-foreground leading-relaxed">
              We&apos;ve invited {teamMemberName.split(' ')[0]}. Connect with someone else in the meantime?
            </p>

            <button
              type="button"
              onClick={onRequestIntroduction}
              disabled={loading}
              className="w-full border border-border text-foreground rounded-full px-5 py-[9px] text-[13px] hover:bg-accent transition-colors disabled:opacity-50"
            >
              {loading ? 'Requesting...' : `Browse ${config.label} Partners`} &rarr;
            </button>
          </div>
        </div>
      );
    }

    // Exploring options: show current + alternatives
    if (teamMemberStatus === 'exploring_options') {
      return (
        <div className={`bg-card border border-border rounded-[16px] p-6 w-full ${className}`}>
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-500 rounded-full px-3 py-1">
              <Search className="w-3 h-3" />
              <span className="font-mono text-[11px] uppercase tracking-wide">
                Exploring options
              </span>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-500 font-semibold text-sm">
                {teamMemberName.split(' ').map((n) => n[0]).join('')}
              </div>
              <div className="flex-1">
                <div className="text-[14px] text-foreground font-medium leading-tight">
                  {teamMemberName}
                </div>
                {teamMemberFirm && (
                  <div className="text-[13px] text-muted-foreground">
                    {teamMemberFirm}
                  </div>
                )}
              </div>
            </div>

            <p className="text-[14px] text-muted-foreground leading-relaxed">
              You&apos;re exploring options for {config.label.toLowerCase()}. {teamMemberName.split(' ')[0]} is your current {config.label.toLowerCase()}.
            </p>

            <button
              type="button"
              onClick={onRequestIntroduction}
              disabled={loading}
              className="w-full bg-[hsl(var(--succeedo-orange))] text-white rounded-lg py-3 font-medium text-[14px] hover:bg-[hsl(var(--succeedo-orange))]/90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Requesting...' : 'Browse Alternatives'}
            </button>
          </div>
        </div>
      );
    }
  }

  // Priority 1-4: existing states (UNCHANGED)
  return (
    <div className={`bg-card border border-border rounded-[16px] p-6 w-full ${className}`}>
      {/* STATE 1: Assessment-Context CTA */}
      {state === 'assessment-context' && (
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 bg-[hsl(var(--succeedo-orange))]/10 border border-[hsl(var(--succeedo-orange))]/20 text-[hsl(var(--succeedo-orange))] rounded-full px-3 py-1">
            <div className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--succeedo-orange))]" />
            <span className="font-mono text-[11px] uppercase tracking-wide">
              Based on your assessment
            </span>
          </div>

          <h3 className="text-[17px] font-semibold text-foreground leading-tight">
            Ready to connect with a {config.label}?
          </h3>

          <p className="text-[14px] text-muted-foreground leading-relaxed">
            {assessmentScore
              ? `Your score was ${assessmentScore}% — ${config.description.charAt(0).toLowerCase()}${config.description.slice(1)}`
              : config.description}
          </p>

          <button
            type="button"
            onClick={onRequestIntroduction}
            disabled={loading}
            className="w-full bg-[hsl(var(--succeedo-orange))] text-white rounded-lg py-3 font-medium text-[14px] hover:bg-[hsl(var(--succeedo-orange))]/90 transition-colors disabled:opacity-50"
          >
            {loading ? 'Requesting...' : 'Request Introduction'}
          </button>

          <button
            type="button"
            className="w-full text-muted-foreground/40 text-[13px] hover:text-muted-foreground/60 transition-colors"
          >
            {config.learnMore} &rarr;
          </button>
        </div>
      )}

      {/* STATE 2: Generic CTA */}
      {state === 'generic' && (
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 border border-border text-muted-foreground/30 rounded-full px-3 py-1">
            <span className="font-mono text-[11px] uppercase tracking-wide">
              Next step
            </span>
          </div>

          <h3 className="text-[17px] font-semibold text-foreground leading-tight">
            Ready to connect with a {config.label}?
          </h3>

          <p className="text-[14px] text-muted-foreground leading-relaxed">
            {config.genericDescription}
          </p>

          <button
            type="button"
            onClick={onRequestIntroduction}
            disabled={loading}
            className="w-full bg-[hsl(var(--succeedo-orange))] text-white rounded-lg py-3 font-medium text-[14px] hover:bg-[hsl(var(--succeedo-orange))]/90 transition-colors disabled:opacity-50"
          >
            {loading ? 'Requesting...' : `Connect with a ${config.label}`}
          </button>
        </div>
      )}

      {/* STATE 3: Introduction Pending */}
      {state === 'pending' && (
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 bg-[hsl(var(--chart-2))]/10 border border-[hsl(var(--chart-2))]/30 text-[hsl(var(--chart-2))] rounded-full px-3 py-1">
            <div className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--chart-2))]" />
            <span className="font-mono text-[11px] uppercase tracking-wide">
              Introduction in progress
            </span>
          </div>

          <h3 className="text-[17px] font-semibold text-foreground leading-tight">
            We&apos;re finding your {config.label.toLowerCase()}
          </h3>

          <p className="text-[14px] text-muted-foreground leading-relaxed">
            You&apos;ve already requested a {config.label.toLowerCase()} introduction. Our team is reviewing your profile and connecting you with the right match for your business needs.
          </p>

          {requestedTime && (
            <div className="font-mono text-[11px] text-muted-foreground/35 uppercase tracking-wide">
              Requested {requestedTime}
            </div>
          )}

          <div className="flex items-center gap-2 text-[hsl(var(--chart-2))] py-3">
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-[14px] font-medium">Introduction Requested</span>
          </div>
        </div>
      )}

      {/* STATE 4: Connected */}
      {state === 'connected' && (
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 border border-[hsl(var(--chart-2))] text-[hsl(var(--chart-2))] rounded-full px-3 py-1">
            <CheckCircle2 className="w-3 h-3" />
            <span className="font-mono text-[11px] uppercase tracking-wide">
              Connected
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-foreground font-semibold text-sm">
              {partnerName ? partnerName.split(' ').map((n) => n[0]).join('') : '??'}
            </div>
            <div className="flex-1">
              <div className="text-[14px] text-foreground font-medium leading-tight">
                {partnerName || 'Your Partner'}
              </div>
              {partnerFirm && (
                <div className="text-[13px] text-muted-foreground">
                  {partnerFirm}
                </div>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={() => { window.location.href = '/succeedo/teams'; }}
            className="w-full border border-border text-foreground rounded-full px-5 py-[9px] text-[13px] hover:bg-accent transition-colors"
          >
            View Your Team &rarr;
          </button>
        </div>
      )}
    </div>
  );
}
